import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Crown, Code2, Copy, CheckCircle2 } from "lucide-react";
import Wordmark from "../components/Wordmark";
import SeverityBadge from "../components/SeverityBadge";
import QuantumScoreDial from "../components/QuantumScoreDial";
import api from "../lib/api";
import { useUserAuth } from "../lib/userAuth";

import useDocumentTitle from "../hooks/useDocumentTitle";

export default function ReportPage() {
  useDocumentTitle('Scan Report — QorTrace');
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [error, setError] = useState("");
  const { account } = useUserAuth();
  const plan = (account?.plan || "free").toLowerCase();
  const isPaid = plan === "pro" || plan === "team" || plan === "enterprise";

  useEffect(() => {
    api.report(id)
      .then(setR)
      .catch((e) => setError(e?.response?.data?.detail || "Report not found"));
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen qt-radial-bg" style={{ padding: 40 }}>
        <div className="qt-card max-w-xl">{error}</div>
      </div>
    );
  }
  if (!r) return <div className="min-h-screen qt-radial-bg" style={{ padding: 40 }}>Loading…</div>;

  const isScan = r.type === "scan";

  return (
    <div className="min-h-screen qt-radial-bg" data-testid="report-page">
      {/* Free-plan watermark (CSS-only, print-activated). Absent in DOM for
          Pro/Team so the PDF is fully clean. */}
      {!isPaid && (
        <>
          <div className="qt-watermark-free-banner" data-testid="watermark-banner">
            FREE PREVIEW · UPGRADE TO PRO FOR A WATERMARK-FREE REPORT · qortrace.com/pricing
          </div>
          <div className="qt-watermark-free" data-testid="watermark-overlay" aria-hidden="true" />
        </>
      )}

      {/* Top toolbar (hidden on print) */}
      <div className="qt-no-print sticky top-0 z-40 backdrop-blur-xl"
           style={{ background: "rgba(4,9,20,0.75)", borderBottom: "1px solid var(--qt-border)" }}>
        <div className="max-w-[1100px] mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to={isScan ? "/scan" : "/contract"} className="qt-btn qt-btn-ghost" style={{ padding: "6px 10px" }}>
              <ArrowLeft size={14}/> Back
            </Link>
            <Wordmark size={18} />
            <span className="qt-label hidden sm:inline">{isScan ? "WALLET REPORT" : "CONTRACT REPORT"}</span>
            {isPaid && (
              <span className="qt-mono text-[10px] ml-2 px-2 py-0.5 rounded"
                    data-testid="report-pro-badge"
                    style={{ color: "#1FDD83", border: "1px solid rgba(0,210,106,0.4)", background: "rgba(0,210,106,0.08)", letterSpacing: "0.15em" }}>
                WATERMARK-FREE · {plan.toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isPaid && (
              <Link to="/pricing"
                    data-testid="report-upgrade-cta"
                    className="qt-btn qt-btn-ghost"
                    style={{ fontSize: 12, padding: "6px 10px", border: "1px solid rgba(255,110,110,0.4)", color: "#FF6E6E" }}>
                <Crown size={12} /> Remove watermark
              </Link>
            )}
            <button data-testid="print-button"
                    onClick={() => window.print()} className="qt-btn qt-btn-primary">
              <Printer size={14}/> Save as PDF
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 sm:px-8 py-10 qt-print-section">
        {/* Cover */}
        <div className="qt-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <Wordmark size={26} />
            <div className="qt-mono text-xs" style={{ color: "var(--qt-text-3)" }}>
              REPORT ID · {r.id}<br/>
              GENERATED · {new Date(r.created_at).toISOString().replace("T", " ").slice(0, 19)} UTC<br/>
              METHODOLOGY · {r.methodology_version}
            </div>
          </div>
          <div className="mt-6">
            <div className="qt-label">{isScan ? "WALLET QUANTUM EXPOSURE REPORT" : "SMART CONTRACT PQC READINESS REPORT"}</div>
            <h1 className="qt-h2 text-3xl mt-2">
              {isScan ? `${r.chain_label} · ${r.address.slice(0, 14)}…${r.address.slice(-6)}` : (r.name || "Contract analysis")}
            </h1>
          </div>
        </div>

        {/* Body — scan */}
        {isScan && <ScanBody r={r} />}

        {/* Embeddable badge (scan reports only) — Phase 4.13.3 */}
        {isScan && <BadgeEmbedBlock r={r} />}

        {/* Body — contract */}
        {!isScan && <ContractBody r={r} />}

        <div className="mt-10 qt-mono text-xs" style={{ color: "var(--qt-text-3)", textAlign: "center" }}>
          generated by qortrace.com · methodology {r.methodology_version} · report ID {r.id}
        </div>
      </div>
    </div>
  );
}

function ScanBody({ r }) {
  const d = r.data || {};
  const sc = r.scoring || {};
  const adv = r.advisor || {};
  return (
    <>
      <div className="qt-card mt-6 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
        <div className="md:col-span-5 flex justify-center">
          <QuantumScoreDial score={sc.score} tier={sc.tier} size={260} />
        </div>
        <div className="md:col-span-7 grid grid-cols-2 gap-3">
          <KV label="CHAIN"          v={d.chain_label} />
          <KV label="ADDRESS TYPE"   v={d.address_type} />
          <KV label="BALANCE"        v={`${(d.balance_native ?? 0).toLocaleString(undefined, { maximumFractionDigits: 6 })} ${d.symbol}`} />
          <KV label="BALANCE (USD)"  v={`$${(d.balance_usd ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`} />
          <KV label="TX COUNT"       v={(d.tx_count ?? 0).toLocaleString()} />
          <KV label="PUBKEY EXPOSED" v={d.pubkey_exposed ? "YES" : "NO"} />
          <div className="col-span-2 qt-mono text-xs" style={{ color: "var(--qt-text-3)" }}>
            ADDRESS · {d.address}
          </div>
        </div>
      </div>

      {adv.executive_summary && (
        <div className="qt-card mt-6">
          <div className="qt-label">EXECUTIVE SUMMARY</div>
          <p className="mt-2 text-base" style={{ color: "#fff", lineHeight: 1.6 }}>{adv.executive_summary}</p>
          {adv.key_risks?.length > 0 && (
            <ul className="mt-4 space-y-2 text-sm" style={{ color: "var(--qt-text-2)" }}>
              {adv.key_risks.map((x, i) => (
                <li key={i} className="flex gap-2"><span style={{ color: "var(--qt-amber)" }}>◆</span><span>{x}</span></li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="qt-card mt-6">
        <div className="qt-label">DETAILED FACTOR BREAKDOWN</div>
        <table className="w-full text-sm mt-3">
          <thead>
            <tr><th className="qt-label text-left pb-2">FACTOR</th><th className="qt-label text-left pb-2">SCORE</th><th className="qt-label text-left pb-2">EVIDENCE</th></tr>
          </thead>
          <tbody>
            {(sc.factors || []).map((f, i) => (
              <tr key={i} style={{ borderTop: "1px solid var(--qt-border)" }}>
                <td className="py-2 pr-3 font-semibold" style={{ color: "#fff" }}>{f.label}</td>
                <td className="py-2 pr-3 qt-mono" style={{ color: "var(--qt-cyan)" }}>{f.score} / {f.weight_max}</td>
                <td className="py-2 text-xs" style={{ color: "var(--qt-text-2)", lineHeight: 1.5 }}>{f.evidence}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {adv.migration_playbook?.length > 0 && (
        <div className="qt-card mt-6">
          <div className="qt-label">MIGRATION PLAYBOOK</div>
          <div className="mt-3 space-y-3">
            {adv.migration_playbook.map((s) => (
              <div key={s.step} className="qt-card" style={{ padding: 14, background: "rgba(4,9,20,0.5)" }}>
                <div className="qt-mono text-xs" style={{ color: "var(--qt-cyan)" }}>STEP {s.step} · {s.title}</div>
                <p className="mt-1 text-sm" style={{ color: "var(--qt-text-2)" }}>{s.detail}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function ContractBody({ r }) {
  const adv = r.advisor || {};
  const summary = r.summary || {};
  return (
    <>
      <div className="qt-card mt-6 grid grid-cols-2 md:grid-cols-6 gap-3">
        <Box label="VERDICT"  v={r.verdict} />
        <Box label="CRITICAL" v={summary.Critical || 0} />
        <Box label="HIGH"     v={summary.High || 0} />
        <Box label="MEDIUM"   v={summary.Medium || 0} />
        <Box label="INFO"     v={summary.Info || 0} />
        <Box label="LOC"      v={r.loc} />
      </div>

      {adv.executive_summary && (
        <div className="qt-card mt-6">
          <div className="qt-label">EXECUTIVE SUMMARY</div>
          <p className="mt-2 text-base" style={{ color: "#fff", lineHeight: 1.6 }}>{adv.executive_summary}</p>
        </div>
      )}

      <div className="qt-card mt-6">
        <div className="qt-label">DETAILED FINDINGS</div>
        <div className="mt-3 space-y-3">
          {(r.findings || []).map((f, i) => (
            <div key={i} className="qt-card" style={{ padding: 14, background: "rgba(4,9,20,0.5)" }}>
              <div className="flex items-center gap-3 flex-wrap">
                <SeverityBadge severity={f.severity} />
                <span className="font-semibold text-sm">{f.label}</span>
                <span className="qt-mono text-xs" style={{ color: "var(--qt-text-3)" }}>L{f.line}</span>
              </div>
              <div className="text-sm mt-2" style={{ color: "var(--qt-text-2)" }}>{f.description}</div>
              <pre className="mt-2 px-3 py-2 rounded qt-mono text-xs"
                   style={{ background: "rgba(4,9,20,0.85)", border: "1px solid var(--qt-border)", color: "#9CAEC9", overflowX: "auto" }}>
                {f.snippet}
              </pre>
              <div className="text-xs mt-2" style={{ color: "var(--qt-cyan)" }}>
                <span className="qt-label" style={{ color: "var(--qt-cyan)" }}>MIGRATION ›</span> {f.migration}
              </div>
            </div>
          ))}
        </div>
      </div>

      {adv.migration_playbook?.length > 0 && (
        <div className="qt-card mt-6">
          <div className="qt-label">MIGRATION PLAYBOOK</div>
          <div className="mt-3 space-y-3">
            {adv.migration_playbook.map((s) => (
              <div key={s.step} className="qt-card" style={{ padding: 14, background: "rgba(4,9,20,0.5)" }}>
                <div className="qt-mono text-xs" style={{ color: "var(--qt-cyan)" }}>STEP {s.step} · {s.title}</div>
                <p className="mt-1 text-sm" style={{ color: "var(--qt-text-2)" }}>{s.detail}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function KV({ label, v }) {
  return (
    <div className="qt-card" style={{ padding: 12, background: "rgba(4,9,20,0.5)" }}>
      <div className="qt-label">{label}</div>
      <div className="qt-mono mt-1" style={{ color: "#fff", fontSize: 16, fontWeight: 600 }}>{v || "—"}</div>
    </div>
  );
}
function Box({ label, v }) {
  return (
    <div className="qt-card" style={{ padding: 12, background: "rgba(4,9,20,0.5)" }}>
      <div className="qt-label">{label}</div>
      <div className="qt-mono mt-1" style={{ color: "#fff", fontSize: 22, fontWeight: 700, letterSpacing: "-0.04em" }}>{v}</div>
    </div>
  );
}

function BadgeEmbedBlock({ r }) {
  const [style, setStyle] = useState("pill");
  const [copiedKey, setCopiedKey] = useState("");
  const origin = window.location.origin;
  const imgUrl = `${origin}/api/badge/${r.chain}/${r.address}/score.svg?style=${style}`;
  // Click-through goes through our attribution endpoint so we can measure
  // which embedding domains drive real traffic (Phase 4.13.5).
  const clickUrl = `${origin}/api/badge/${r.chain}/${r.address}/click`;
  const htmlSnippet = `<a href="${clickUrl}"><img src="${imgUrl}" alt="QorTrace PQC score" /></a>`;
  const mdSnippet = `[![QorTrace PQC](${imgUrl})](${clickUrl})`;

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 2000);
    } catch (_) {}
  };

  return (
    <div className="qt-card mt-6 qt-no-print" data-testid="badge-embed-block">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>
            <Code2 size={12} className="inline -mt-0.5 mr-1" /> EMBEDDABLE BADGE
          </div>
          <div className="qt-h2 text-xl mt-1">Show this score in a README or project site.</div>
          <p className="text-sm mt-1" style={{ color: "var(--qt-text-2)" }}>
            Drop the image URL anywhere — GitHub, Notion, docs. Scores refresh every 6 hours.
          </p>
        </div>
        <div className="flex gap-1 qt-mono text-[10px]" style={{ letterSpacing: "0.12em" }} data-testid="badge-style-toggle">
          {["pill", "card"].map((s) => (
            <button
              key={s}
              onClick={() => setStyle(s)}
              data-testid={`badge-style-${s}`}
              className="px-3 py-1.5 rounded transition-colors"
              style={{
                background: style === s ? "rgba(0,229,255,0.1)" : "transparent",
                border: `1px solid ${style === s ? "rgba(0,229,255,0.4)" : "var(--qt-border)"}`,
                color: style === s ? "var(--qt-cyan)" : "var(--qt-text-2)",
                cursor: "pointer",
              }}
            >
              {s.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Live preview */}
      <div className="mt-4 p-5 rounded flex items-center justify-center"
           style={{ background: "rgba(255,255,255,0.02)", border: "1px dashed var(--qt-border)", minHeight: 110 }}
           data-testid="badge-preview">
        <img src={imgUrl} alt="QorTrace PQC badge preview" />
      </div>

      {/* Snippets */}
      <div className="mt-4 space-y-3">
        <SnippetRow
          testid="badge-snippet-markdown"
          label="MARKDOWN"
          text={mdSnippet}
          copied={copiedKey === "md"}
          onCopy={() => copy("md", mdSnippet)}
        />
        <SnippetRow
          testid="badge-snippet-html"
          label="HTML"
          text={htmlSnippet}
          copied={copiedKey === "html"}
          onCopy={() => copy("html", htmlSnippet)}
        />
        <SnippetRow
          testid="badge-snippet-url"
          label="IMAGE URL"
          text={imgUrl}
          copied={copiedKey === "url"}
          onCopy={() => copy("url", imgUrl)}
        />
      </div>
    </div>
  );
}

function SnippetRow({ testid, label, text, copied, onCopy }) {
  return (
    <div data-testid={testid}>
      <div className="qt-mono text-[10px] mb-1" style={{ color: "var(--qt-text-3)", letterSpacing: "0.15em" }}>
        {label}
      </div>
      <div className="flex gap-2 items-stretch">
        <code className="qt-mono text-xs flex-1 px-3 py-2 rounded"
              style={{ background: "rgba(0,0,0,0.5)", border: "1px solid var(--qt-border)", color: "#fff", overflowX: "auto", whiteSpace: "nowrap" }}>
          {text}
        </code>
        <button
          onClick={onCopy}
          data-testid={`${testid}-copy`}
          className="qt-btn qt-btn-secondary"
          style={{ padding: "6px 10px", fontSize: 11, minWidth: 82, justifyContent: "center" }}
        >
          {copied ? <><CheckCircle2 size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
        </button>
      </div>
    </div>
  );
}
